import { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../CustomAntdComponents/Button";
import { Paths } from "../../../router/paths";
import { localizeTo } from "../../../i18n/localePath";
import { TRIAL_DAYS } from "../../../api/services/pricing-plan.billing";
import {
  trackTrialPromoModalClicked,
  trackTrialPromoModalDismissed,
  trackTrialPromoModalShown,
} from "../../../analytics/amplitude";
// Same ticket-style card as the annual-discount popup, so the two campaigns look like one family.
import styles from "../AnnualDiscountModal/AnnualDiscountModal.module.css";

const OPEN_DELAY_MS = 1000;

interface TrialReadyModalProps {
  open: boolean;
  onDismiss: () => void;
}

/** "Your free trial is ready" popup (docs/free-trial-design.md). */
export const TrialReadyModal = ({ open, onDismiss }: TrialReadyModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [delayedOpen, setDelayedOpen] = useState(false);
  const tracked = useRef(false);

  useEffect(() => {
    if (!open) {
      setDelayedOpen(false);
      tracked.current = false;
      return;
    }
    const timer = window.setTimeout(() => setDelayedOpen(true), OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!delayedOpen || tracked.current) return;
    tracked.current = true;
    trackTrialPromoModalShown();
  }, [delayedOpen]);

  const handleDismiss = () => {
    trackTrialPromoModalDismissed();
    onDismiss();
  };

  const handleCta = () => {
    trackTrialPromoModalClicked();
    onDismiss();
    navigate(localizeTo(Paths.SUBSCRIPTIONS, pathname));
  };

  return (
    <Modal
      open={delayedOpen}
      onCancel={handleDismiss}
      footer={null}
      width={420}
      centered
      closable={false}
      maskClosable={false}
      className={styles.modal}
      styles={{
        body: { padding: 0 },
        content: { padding: 0, overflow: "visible", borderRadius: 20 },
      }}
    >
      <div className={styles.card}>
        <div className={styles.hero}>
          <span className={styles.orbA} aria-hidden />
          <span className={styles.orbB} aria-hidden />
          <button
            type="button"
            className={styles.close}
            onClick={handleDismiss}
            aria-label={t("pages.trialPromo.close")}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <span className={styles.eyebrow}>{t("pages.trialPromo.eyebrow")}</span>
          <div className={styles.percentBlock}>
            <span className={styles.percent}>
              {t("pages.trialPromo.daysMark", { days: TRIAL_DAYS })}
            </span>
            <span className={styles.off}>{t("pages.trialPromo.free")}</span>
          </div>
          <p className={styles.heroLabel}>{t("pages.trialPromo.heroLabel")}</p>
        </div>

        <div className={styles.perforation} aria-hidden>
          <span className={styles.notchLeft} />
          <span className={styles.dash} />
          <span className={styles.notchRight} />
        </div>

        <div className={styles.body}>
          <h2 className={styles.headline}>{t("pages.trialPromo.headline")}</h2>
          <p className={styles.copy}>
            {t("pages.trialPromo.body", { days: TRIAL_DAYS })}
          </p>

          <Button
            type="primary"
            block
            size="large"
            onClick={handleCta}
            className={styles.primary}
          >
            {t("pages.trialPromo.cta")}
          </Button>
          <button type="button" className={styles.later} onClick={handleDismiss}>
            {t("pages.trialPromo.maybeLater")}
          </button>
        </div>
      </div>
    </Modal>
  );
};
